import React from 'react';

import { useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useSelector } from 'react-redux';

import { DISPLAY_TYPES } from 'components/common/UploadModal/utils';
import { countRules } from 'components/releases/Details/components/NoteModal/config';
import { MEDIAS_API_UPLOAD } from 'constants/ApiConstants';
import { MANUFACTURER } from 'constants/UserConstants';
import { getCurrentUserRoles } from 'modules/users/selectors/currentUser';
import { Input, Select } from 'ui/base';
import UploadDragger from 'ui/base/MediasUploader/components/MediaLibrary';
import PaginatedDealerSelect from 'ui/fields/selects/PaginatedDealerSelect';
import PhoneNumberDetailedInput from 'ui/form/PhoneNumberDetailedInput';

import { SUBSCRIPTION } from 'utils/formUtils';

import { MAX_DEFAULT_SUMMARY_LEGEND_LENGTH, REQUEST_RELATES_OPTIONS, YES_NO_OPTIONS, REQUEST_RELATES, UPLOAD_PARAMS, TERNARY_RESPONSE } from './constants';

const formatMedias = (value) => value || [];
const parseMedias = (value) => value?.filter((media) => media.status !== 'DELETED') || [];

const format = v => v;

const formatYesNo = (value) => {
  if (value === true) return TERNARY_RESPONSE.YES;
  if (value === false) return TERNARY_RESPONSE.NO;
  if (value === null) return TERNARY_RESPONSE.I_DONT_KNOW;
  return value;
};

const parseYesNo = (value) => {
  if (value === TERNARY_RESPONSE.YES) return true;
  if (value === TERNARY_RESPONSE.NO) return false;
  if (value === TERNARY_RESPONSE.I_DONT_KNOW) return null;
  return value;
};

/**
 * @param disabled
 */
export const useFieldConfig = (disabled) => {
  const currentUserRoles = useSelector(getCurrentUserRoles);
  const isManufacturer = currentUserRoles.includes(MANUFACTURER);
  const { onChange: setMedias } = useField('medias').input;

  const intl = useIntl();
  const { value: contextType } = useField('context.type', SUBSCRIPTION.VALUE_ONLY).input;

  return [
    {
      name: 'customer.firstName',
      label: (
        <FormattedMessage id="firstName" defaultMessage="First name" />
      ),
      required: true,
      component: <Input disabled={disabled} />
    },
    {
      name: 'customer.lastName',
      label: (
        <FormattedMessage id="lastName" defaultMessage="Last name" />
      ),
      required: true,
      component: <Input disabled={disabled} />
    },
    {
      name: 'customer.phoneNumber',
      subscription: {},
      component: <PhoneNumberDetailedInput required disabled={disabled} />
    },
    {
      name: 'customer.email',
      label: <FormattedMessage id="email" defaultMessage="Email" />,
      required: true,
      component: <Input disabled={disabled} />
    },
    !isManufacturer ? {
      name: 'dealerId',
      label: <FormattedMessage id="page.steps.details.customerDescription.tags.dealership" defaultMessage="Dealership" />,
      required: true,
      format,
      component: (
        <PaginatedDealerSelect
          placeholder={intl.formatMessage({
            id: 'noneSelected',
            defaultMessage: 'None selected'
          })}
          disabled={disabled}
        />)
    } : undefined,
    {
      name: 'context.type',
      label: <FormattedMessage id="reportIssue.requestRelatesTo" defaultMessage="Request relates to" />,
      required: true,
      format,
      component: (
        <Select
          options={REQUEST_RELATES_OPTIONS}
          placeholder={intl.formatMessage({ id: 'noneSelected', defaultMessage: 'None selected' })}
          disabled={disabled}
        />)
    },
    contextType === REQUEST_RELATES.MOBILE_APP ? {
      name: 'context.deviceName',
      label: <FormattedMessage id="reportIssue.deviceName" defaultMessage="Device name" />,
      formItemProps: {
        tooltip: <FormattedMessage
          id="reportIssue.tooltip.deviceName"
          defaultMessage="To fill if you have chosen 'Mobile App' as an answer above."
        />
      },
      component: (<Input disabled={disabled} />)
    } : undefined,
    contextType === REQUEST_RELATES.KIOSK ? {
      name: 'context.kiosk.locationId',
      label: <FormattedMessage id="reportIssue.locationId" defaultMessage="Location ID" />,
      formItemProps: {
        tooltip: <FormattedMessage
          id="reportIssue.tooltip.locationId"
          defaultMessage="Enter the location ID indicated on the kiosk label, if available."
        />
      },
      component: <Input disabled={disabled} />
    } : undefined,
    contextType === REQUEST_RELATES.KIOSK ? {
      name: 'context.kiosk.name',
      label: <FormattedMessage id="reportIssue.kioskName" defaultMessage="Kiosk name" />,
      formItemProps: {
        tooltip: <FormattedMessage
          id="reportIssue.tooltip.kioskName"
          defaultMessage="Fill in with Kiosk name in Fleetback, that can also be found in the home screen of the kiosk."
        />
      },
      component: <Input disabled={disabled} />
    } : undefined,
    {
      name: 'context.recurrence.isRecurring',
      label: <FormattedMessage id="reportIssue.isRecurring" defaultMessage="Is it a recurring issue?" />,
      format: formatYesNo,
      parse: parseYesNo,
      component: (<Select
        options={YES_NO_OPTIONS}
        placeholder={intl.formatMessage({ id: 'noneSelected', defaultMessage: 'None selected' })}
        disabled={disabled}
      />)
    },
    {
      name: 'context.recurrence.affectOthers',
      label: <FormattedMessage id="reportIssue.affectOthers" defaultMessage="Does the issue happens to other people?" />,
      format: formatYesNo,
      parse: parseYesNo,
      component: (
        <Select
          options={YES_NO_OPTIONS}
          placeholder={intl.formatMessage({ id: 'noneSelected', defaultMessage: 'None selected' })}
          disabled={disabled}
        />
      )
    },
    {
      name: 'issue.summary',
      label: <FormattedMessage id="reportIssue.summary" defaultMessage="Summary of the request" />,
      required: true,
      component: (
        <Input
          count={countRules}
          disabled={disabled}
          maxLength={MAX_DEFAULT_SUMMARY_LEGEND_LENGTH}
          placeholder={intl.formatMessage({ id: 'reportIssue.placeholder.summary', defaultMessage: 'Please describe the request in a few words.' })}
        />
      )
    },
    {
      name: 'issue.description',
      label: <FormattedMessage id="reportIssue.description" defaultMessage="Description of the request" />,
      required: true,
      component: (<Input.TextArea
        disabled={disabled}
        autoSize={{ minRows: 3 }}
        placeholder={intl.formatMessage({
          id: 'reportIssue.placeholder.description',
          defaultMessage: 'If your request relates to a specific file, please provide its reference or URL. Describe the steps to reproduce the issue (if applicable), indicate where it occurs, and specify the mode in which it happens (After-sales, Sales, etc.).'
        })}
      />
      )
    },
    {
      name: 'medias',
      label: <FormattedMessage id="reportIssue.medias" defaultMessage="Screenshots and screen recordings" />,
      format: formatMedias,
      parse: parseMedias,
      component: ({ value, ...props }) => (
        <>
          <div className="text-grey-50">
            <FormattedMessage
              id="reportIssue.subtitle.medias"
              defaultMessage="Please join any screen, error message or file that could help us handle the request."
            />
          </div>
          <UploadDragger medias={value} {...props} />
        </>
      ),
      componentProps: {
        className: 'mt-3',
        params: UPLOAD_PARAMS,
        uploadURL: MEDIAS_API_UPLOAD,
        delegateToForm: true,
        disabled,
        onChange: setMedias,
        allowed: { path: ['helpdesk', 'medias'] },
        displayTypes: [DISPLAY_TYPES.COMPUTER]
      }
    }
  ].filter(Boolean);
};
